import React, { useMemo } from 'react';
import { IAlbum } from '@/data/models/album';
import { useTranslation } from 'react-i18next';
import './PriceTab.scss';
import { PriceRowSection, usePriceTab } from '@/hooks/usePriceTab';
import { ErrorType, IValidationResults } from '@/data/models/validation';
import ContentAlert from '@/components/common/ErrorAlert/ContentAlert';
import { IChangeRequest } from '@/data/models/changeRequest';
import { isValidationWarning } from '@/utils/validationErrors';
import { ICurrentPricingChangesGroup } from '@/types/product-current-changes-types';
import ChangeRequestIndicator from '@/components/common/ChangeRequestIndicator/ChangeRequestIndicator';
import { usePricingCurrentChanges } from '@/hooks/usePricingCurrentChanges';

interface PriceTabProps {
  album: IAlbum;
  readonly?: boolean;
  togglePricingModal?: () => void;
  errors?: IValidationResults[];
  changeRequest?: IChangeRequest | undefined;
  currentChanges?: ICurrentPricingChangesGroup;
}

const INLINE_PRICING_ISSUE_TYPES = [
  ErrorType.MissingAllowedSalesTeamPrice,
  ErrorType.MissingPrimarySalesTeam,
];

const getInlinePricingIssueClass = (issue: IValidationResults) =>
  isValidationWarning(issue) ? 'pricing-text-warning' : 'text-danger';

export default function PriceTab({
  album,
  readonly,
  togglePricingModal,
  errors = [],
  changeRequest,
  currentChanges,
}: PriceTabProps) {
  const { t } = useTranslation();
  const {
    allRows,
    error,
    allowedSalesTeams,
    allSalesTeams,
    allowedSalesTeamsFetched,
  } = usePriceTab(album, changeRequest);
  const hasAllowedSalesTeams = allowedSalesTeams.length > 0;
  const hasNoTerritoryRestrictions =
    (album.albumMetaData?.territoryRestrictions ?? []).length === 0;
  const showSetAreaRestrictionsInfo =
    allowedSalesTeamsFetched &&
    !error &&
    hasNoTerritoryRestrictions &&
    !hasAllowedSalesTeams;

  const pricingIssues = useMemo(() => {
    return errors.filter((e) => e.fieldName === 'AlbumSalesTeams');
  }, [errors]);

  const pricingBannerIssues = useMemo(() => {
    if (showSetAreaRestrictionsInfo) return [];

    return pricingIssues
      .filter(
        (error) =>
          !error.errorType ||
          !INLINE_PRICING_ISSUE_TYPES.includes(error.errorType)
      )
      .filter((error) => !!error.validationMessage);
  }, [pricingIssues, showSetAreaRestrictionsInfo]);

  const missingAllowedSalesTeamPriceIssue = useMemo(
    () =>
      pricingIssues.find(
        (error) => error.errorType === ErrorType.MissingAllowedSalesTeamPrice
      ),
    [pricingIssues]
  );
  const missingPrimarySalesTeamIssue = useMemo(
    () =>
      pricingIssues.find(
        (error) => error.errorType === ErrorType.MissingPrimarySalesTeam
      ),
    [pricingIssues]
  );

  const openRestrictionsModal = () => {
    if (togglePricingModal) togglePricingModal();
  };

  const pricingButtonDisabled = !hasAllowedSalesTeams;
  const setAreaRestrictionsMessage = t(
    'product.pricing.setAreaRestrictionsToEnableSalesTerritories'
  );
  const pricingButtonTooltip = showSetAreaRestrictionsInfo
    ? setAreaRestrictionsMessage
    : undefined;

  const displayRows = useMemo(() => {
    const primaryRow = allRows.find(
      (row) => row.section === PriceRowSection.Primary
    );

    let filtered = allRows;
    if (primaryRow) {
      filtered = allRows.filter((row) => {
        if (row === primaryRow) return true;
        if (row.isSeparator) return true;
        return row.name !== primaryRow.name;
      });
    }

    const cleaned = [];
    for (let i = 0; i < filtered.length; i++) {
      if (filtered[i].isSeparator) {
        if (i === filtered.length - 1) continue;
        if (filtered[i + 1].isSeparator) continue;
      }
      cleaned.push(filtered[i]);
    }

    if (cleaned.length > 0 && cleaned[0].isSeparator) {
      cleaned.shift();
    }

    const primarySalesTeamPrice = album.albumSalesTeams?.find(
      (t) => t.isPrimary
    );
    const primarySeq = primarySalesTeamPrice?.sequence;

    return cleaned.map((row) => {
      if (row.isSeparator) return row;

      const salesTeamDefinition = allowedSalesTeams.find(
        (st: any) => t(st.salesTeamKey) === row.name
      );
      let isCustomPrice = row.isCustomPrice;

      if (salesTeamDefinition) {
        const ast = album.albumSalesTeams?.find(
          (a) => a.salesTeamId === salesTeamDefinition.id
        );
        if (!ast || ast.price == null) {
          isCustomPrice = false;
        } else {
          isCustomPrice =
            ast.isPrimary || primarySeq == null
              ? false
              : ast.sequence == null ||
                Number(ast.sequence) !== Number(primarySeq);
        }
      }

      return {
        ...row,
        isCustomPrice,
      };
    });
  }, [allRows, t, allowedSalesTeams, album.albumSalesTeams]);

  const currentPrimaryRow = displayRows.find(
    (row) => row.section === PriceRowSection.Primary && !row.isSeparator
  );

  const {
    hasCurrentChanges,
    getPricingChangeBySalesTeamId,
    getOriginalPriceDisplayValue,
    originalPrimarySalesTeamChange,
    originalPrimaryPriceChange,
    originalPrimarySalesTeamName,
    originalPrimarySalesTeam,
  } = usePricingCurrentChanges({
    currentChanges,
    allSalesTeams,
    currentPrimarySalesTeamId: currentPrimaryRow?.salesTeamId,
  });

  return (
    <section className="restrictions-view">
      {showSetAreaRestrictionsInfo ? (
        <div className="alert alert-info pricing-info-banner" role="status">
          <i className="icon fas fa-info-circle" aria-hidden="true"></i>
          <span>{setAreaRestrictionsMessage}</span>
        </div>
      ) : null}
      {pricingBannerIssues.map((issue, index) => (
        <ContentAlert
          key={index}
          contentText={issue.validationMessage}
          dismissable={false}
          variant={isValidationWarning(issue) ? 'warning' : 'danger'}
        />
      ))}
      <div className="button-bar clearfix">
        <span className="restrictions-info-title">
          <span className="restrictions-info-title-strong">
            {error ? (
              <i className="icon fas fa-exclamation-triangle"></i>
            ) : null}
            <span>{t('product.pricing.salesTerritories')}</span>
          </span>
        </span>
        {!readonly ? (
          <span
            className="pricing-action-wrapper float-end"
            title={pricingButtonTooltip}
          >
            <button
              className={`btn btn-primary alt ${
                pricingButtonDisabled ? 'pricing-action-disabled' : ''
              }`}
              onClick={() => openRestrictionsModal()}
              disabled={pricingButtonDisabled}
            >
              <i className={'icon fas fa-pencil'}></i>
              <span>{t('product.pricing.editPricing')}</span>
            </button>
          </span>
        ) : null}
      </div>
      <div className="pricing-container">
        <div className="price-table">
          <div className="price-table-header">
            <div className="price-table-cell header-cell"></div>
            <div className="price-table-cell header-cell">
              {t('product.pricing.salesTerritoryName')}
            </div>
            <div className="price-table-cell header-cell">
              {t('product.pricing.currency')}
            </div>
            <div className="price-table-cell header-cell">
              {t('product.pricing.ppd')}
            </div>
          </div>
          <div className="price-table-body">
            {displayRows.length > 0 ? (
              displayRows.map((item, index) => {
                const isInactive = item.section === PriceRowSection.Inactive;
                const isPrimarySalesTeamRow =
                  item.section === PriceRowSection.Primary;
                const pricingChange =
                  hasCurrentChanges && item.salesTeamId != null
                    ? getPricingChangeBySalesTeamId(item.salesTeamId)
                    : undefined;
                const rowPriceChange = isPrimarySalesTeamRow
                  ? originalPrimaryPriceChange
                  : pricingChange;
                const showPriceChange = isPrimarySalesTeamRow
                  ? Boolean(
                      originalPrimarySalesTeamChange?.isPrimary.hasChanges ||
                        originalPrimaryPriceChange?.price.hasChanges
                    )
                  : Boolean(pricingChange?.price.hasChanges);
                const originalPriceCurrency = isPrimarySalesTeamRow
                  ? originalPrimarySalesTeam?.currencyName ?? item.currency
                  : item.currency;
                const showPrimaryPpdRequired =
                  isPrimarySalesTeamRow &&
                  missingPrimarySalesTeamIssue &&
                  !item.name;
                const showAllowedPpdRequired =
                  item.price === '--' &&
                  missingAllowedSalesTeamPriceIssue &&
                  !isInactive;
                const inlinePricingIssue = showPrimaryPpdRequired
                  ? missingPrimarySalesTeamIssue
                  : showAllowedPpdRequired
                    ? missingAllowedSalesTeamPriceIssue
                    : undefined;

                return item.isSeparator ? (
                  <div
                    className="price-table-row separator-row"
                    key={index}
                  ></div>
                ) : (
                  <div className="price-table-row" key={index}>
                    <div className="price-table-cell">
                      <strong>{item.type}</strong>
                    </div>
                    <div className="price-table-cell">
                      <div className="price-display-wrapper">
                        <ChangeRequestIndicator
                          show={
                            isPrimarySalesTeamRow &&
                            originalPrimarySalesTeamChange?.isPrimary.hasChanges
                          }
                          displayValue={originalPrimarySalesTeamName}
                          customClass="price-tab-cr-indicator"
                        />
                        <span>{item.name.length > 0 ? item.name : '--'}</span>
                      </div>
                    </div>
                    <div className="price-table-cell">{item.currency}</div>
                    <div className="price-table-cell">
                      <div className="price-display-wrapper">
                        <ChangeRequestIndicator
                          show={showPriceChange}
                          displayValue={getOriginalPriceDisplayValue(
                            rowPriceChange?.price.value,
                            originalPriceCurrency
                          )}
                          customClass="price-tab-cr-indicator"
                        />
                        <div>
                          {inlinePricingIssue ? (
                            <span
                              className={getInlinePricingIssueClass(
                                inlinePricingIssue
                              )}
                              title={inlinePricingIssue.validationMessage}
                            >
                              {t('validationAlert.required.message')}
                            </span>
                          ) : (
                            item.price
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="price-table-cell">
                      <div className="warning-icon-wrapper">
                        {item.isCustomPrice && (
                          <i
                            className="icon fas fa-circle-exclamation fa-lg warning-icon"
                            title={t('product.pricing.customPriceOverride')}
                          ></i>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="price-table-row no-data">
                <div className="price-table-cell no-data-message">
                  {t('product.pricing.noDataToDisplay')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
